import axios from "axios";
import { createStore } from "zustand/vanilla";
import { useEffect, useState } from "react";
import { getCookie } from "../../../../utils/cookieUtils";
import { errorNoti } from "../../../../base/BasicNotifications";
import {
  ACCESS_TOKEN_COOKIE_KEY,
  ADMIN_ACCESS_TOKEN_COOKIE_KEY,
  ADMINS_COACH_ID_COOKIE_KEY,
} from "../../../../data/enums/TokenEnums";
import { MILESTONES } from "../ExecutionBlueprintHelpers";
import {
  executionBlueprintUrls,
  LOCKED_ENDPOINTS_READY,
} from "./ExecutionBlueprintUrls";

// ── File downloads ──────────────────────────────────────────────────────────
// ⚠️ Both helpers are Strategy Canvas's, copied rather than imported
// (`StrategyCanvasStore.js` exports neither, and reaching into another
// feature's store for a utility would couple them for no gain). Keeping them
// identical is deliberate: a coach downloading a Canvas PDF and an Execution
// Blueprint PDF should not be able to tell that two different people wrote the
// code.

// Saves a blob response as a normal file download via a throwaway <a> tag.
const triggerFileDownload = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

// A download endpoint's error body is JSON, but because the request set
// `responseType: "blob"` axios hands that body back as a Blob as well — so it
// has to be read as text and re-parsed to recover the real message. Without
// this every failure reads as the generic fallback.
const parseBlobErrorMessage = async (error, fallback) => {
  const data = error.response?.data;
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text());
      return parsed.message || fallback;
    } catch (_) {
      return fallback;
    }
  }
  return error.response?.data?.message || fallback;
};

const nodeInstance = axios.create({
  baseURL: process.env.REACT_APP_NEST_BASE_URL,
});

const getInitials = (companyName = "") =>
  companyName
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join("");

// Maps one dashboard row (COACH-API.md §2) to the props the SME card / steps
// components render. Three field-presence traps baked in here so nothing
// downstream has to know about them:
//
// - `execution_blueprint_unique_id` is ABSENT (not null) pre-start - reading
//   it with `?? null` handles "missing key" and "null" identically, which is
//   all we need.
// - `items_confirmed`/`total_items` are only non-null in the
//   interview-started-but-not-sent-to-coach window; null means "don't render
//   a count," not zero, so the check is explicit rather than `if (count)`.
// - `timestamps` is null, not [], before the blueprint starts.
// There is no coach-facing "Brief" screen for Execution Blueprint (see
// COACH-API.md's endpoint list - only Review Mode and Blueprint Mode exist).
// So unlike Strategy Canvas, the dashboard button skips straight to Review
// Mode once the blueprint reaches SENT_TO_COACH, rather than going through an
// intermediate screen first.
//
// ─────────────────────────────────────────────────────────────────────────────
// Stage -> destination. This replaces an earlier version that hard-disabled
// the button on EVERY stage except SENT_TO_COACH, which had the side effect of
// ignoring the backend's own `button.enabled` entirely - so a row arriving as
// `{"label": "Start Live Review", "enabled": true}` still rendered disabled.
// That was the bug. The rule now: the backend decides the label and whether
// it's enabled; the FE only ever *subtracts*, and only for a stage that
// genuinely has nowhere to navigate in this repo yet.
//
// Both review-related stages route to the review screen, not to Live Review
// directly. That's deliberate and is what makes "you can't get into Live
// Review without starting it" true: Live Review is started from inside the
// review screen, so REVIEW_SUBMITTED's "Start Live Review" button lands the
// coach where the start action actually lives.
// ─────────────────────────────────────────────────────────────────────────────
const REVIEW_READY_STAGE = "SENT_TO_COACH";
const REVIEW_SUBMITTED_STAGE = "REVIEW_SUBMITTED";
const LIVE_REVIEW_STARTED_STAGE = "LIVE_REVIEW_STARTED";
// The last milestone. Its screen is the Final Locked Blueprint hub.
const LOCKED_STAGE = "LOCKED";

export const EXW_DESTINATION = {
  REVIEW: "review",
  LIVE_REVIEW: "live-review",
  LOCKED: "locked",
  NONE: "none",
};

const getDestination = (stage) => {
  switch (stage) {
    case REVIEW_READY_STAGE:
    case REVIEW_SUBMITTED_STAGE:
      return EXW_DESTINATION.REVIEW;
    // Already started, so the gate the review screen enforces is satisfied -
    // this is the "Complete Live Review" case and can go straight in.
    case LIVE_REVIEW_STARTED_STAGE:
      return EXW_DESTINATION.LIVE_REVIEW;
    // LOCKED now HAS somewhere to go (2026-08-29): the Final Locked Blueprint
    // hub, and its three read-only views. The note that used to sit here saying
    // there was "nowhere to send them" is historical — that was true only while
    // the read-only view lived inside the review screen as a mode toggle.
    case LOCKED_STAGE:
      return EXW_DESTINATION.LOCKED;
    // Everything earlier (synthesis / interview / critique) has a backend
    // label of "View brief" - a screen that does not exist coach-side in this
    // contract. Nowhere to send them, so the button stays inert whatever the
    // backend says.
    default:
      return EXW_DESTINATION.NONE;
  }
};

// A disabled control has to say why (see CLAUDE.md - the dashboard card wraps
// this in a StandardTooltip). Empty string means the button is live, so the
// call site can skip the tooltip wrapper entirely.
const getDisabledReason = (stage, destination, backendEnabled, blueprintId) => {
  if (destination === EXW_DESTINATION.NONE) {
    if (stage === "LOCKED") {
      return "This blueprint is locked. A read-only view isn't available from here yet.";
    }
    return "The CEO is still building this blueprint. It'll open once it's sent to you for review.";
  }
  if (!blueprintId) {
    return "This client doesn't have a blueprint yet.";
  }
  if (!backendEnabled) {
    return "Not available yet.";
  }
  return "";
};

const mapDashboardClient = (client) => {
  const hasItemCounts =
    client.items_confirmed !== null &&
    client.items_confirmed !== undefined &&
    client.total_items !== null &&
    client.total_items !== undefined;

  const blueprintId = client.execution_blueprint_unique_id ?? null;
  const isReviewStage = client.stage === REVIEW_READY_STAGE;
  const destination = getDestination(client.stage);
  // The backend omits `enabled` on some rows; absent is treated as enabled,
  // since the label it sends alongside is always an actionable one.
  const backendEnabled = client.button?.enabled !== false;

  return {
    id: client.company_id,
    blueprintId,
    isReviewStage,
    destination,
    // `live_review_step` arrived on this payload on 2026-08-31. It is what lets
    // the dashboard send a coach STRAIGHT to the stage a session has reached —
    // before, the card could only open the Live Review screen, which joined the
    // socket and then bounced onward, so resuming at the Year 1 stage meant
    // watching two screens flash past. Absent on every row that isn't live, and
    // `?? null` handles "key missing" and "null" identically.
    liveReviewStep: client.live_review_step ?? null,
    initials: getInitials(client.company_name),
    name: client.company_name,
    ceo: `CEO: ${client.ceo_name}`,
    stage: client.stage,
    // stage_index's 0-vs-1-based convention against the 11-milestone list is
    // an open question (COACH-API.md §5 #7) - passed through as-is and
    // clamped for display in ExecutionBlueprintHelpers rather than adjusted
    // here, so the raw value stays visible if that assumption turns out wrong.
    filled: client.stage_index ?? 0,
    // Our milestone wording wins over the API's `stage_label` (still
    // "Critique ready for CEO" and the like); the label is only used if the
    // index is out of range.
    statusText:
      MILESTONES[(client.stage_index ?? 0) - 1]?.label ||
      client.stage_label ||
      "Not started",
    statusSubText: hasItemCounts
      ? `${client.items_confirmed} out of ${client.total_items} items`
      : undefined,
    // Enabled when the backend says so, we have a blueprint id to navigate
    // with, AND there's a screen to navigate to. The blueprint-id check isn't
    // redundant: a row can reach a review stage with the id key absent.
    briefDisabled:
      !backendEnabled || !blueprintId || destination === EXW_DESTINATION.NONE,
    // SENT_TO_COACH overrides the backend's label ("Complete Execution
    // Blueprint Review") with "Start Review", because in this repo the button
    // opens review rather than completing it - there's no brief screen in
    // between, so the backend's copy describes the wrong action. Every other
    // stage uses the backend's own label verbatim, "Start Live Review"
    // included.
    briefDisabledReason: getDisabledReason(
      client.stage,
      destination,
      backendEnabled,
      blueprintId,
    ),
    buttonText: isReviewStage
      ? "Start Review"
      : client.button?.label || "View brief",
    // Raw timestamps, not pre-formatted into a positional array like
    // Strategy Canvas does - ExecutionBlueprintSteps looks these up by
    // `key` against the fixed MILESTONES list, which is robust whether this
    // array carries all 11 keys or only the ones reached so far (open
    // question, COACH-API.md §5).
    timestamps: client.timestamps || [],
  };
};

// ─── Post-lock payload adapter ───────────────────────────────────────────────
// Every EXW payload in this codebase arrives as `{ message, data }`, but the
// three post-lock endpoints are undocumented, so this tolerates that and a flat
// body rather than throwing on the shape we guessed wrong about.
const unwrap = (response) => response?.data?.data ?? response?.data ?? null;

// ═════════════════════════════════════════════════════════════════════════════
// The ONE place the post-lock response shapes are read.
//
// The shared locked components (`_shared/LockedBlueprintView.js` and the
// builders it calls) were written against the live socket snapshot and are NOT
// changed to suit REST — they are byte-identical across two repos, and forking
// them so each app's transport could dictate their prop shape is exactly the
// drift that invariant exists to prevent. So this adapts REST to what they
// already accept.
//
// ── WHY IT IS THIS SHAPE-AGNOSTIC ───────────────────────────────────────────
// The real payload (seen 2026-08-31) is NOT what the socket sent, in two ways
// that each blanked a tab on the first run:
//
//   1. `items` is FLAT — one list carrying `section_key` — where the snapshot
//      nested them under `sections[]`. Ungrouped, the Blueprint and Year 1 tabs
//      had nothing to render.
//   2. `connectors` is a FLAT ARRAY OF EDGES where the snapshot sent
//      `{ nodes, edges, overview }`. `buildConnectorGraph` reads
//      `connectors.nodes.items`, so it found none, `hasGraph` came out false,
//      and the WIG Connections tab printed "No connection map was produced" —
//      the exact symptom reported.
//
// So it groups the flat items itself and synthesises the `nodes` block from
// them. It looks for every field across ALL THREE responses rather than in the
// one whose name matches, because the sample body carries `items` AND
// `connectors` together and which endpoint returns what is still unconfirmed.
//
// ⚠️ The payload carries NO BHAG text, though its edges reference a `bhag`
// node. A node is synthesised so those five edges aren't dropped, but it renders
// with an empty label until the backend includes one.
// ═════════════════════════════════════════════════════════════════════════════

// ── canonical section order / labels ────────────────────────────────────────
const SECTION_SEQUENCE = [
  "financial_wig",
  "customer_wig",
  "operations_wig",
  "people_wig",
];
// Plural, matching WIG_SECTIONS' own labels: each titles a list of up to ten
// goals, not one. This map is the fallback for a section the server sends with
// no label of its own, so the two have to read alike.
const SECTION_LABEL = {
  financial_wig: "Financial WIGs",
  customer_wig: "Customer WIGs",
  operations_wig: "Operations WIGs",
  people_wig: "People WIGs",
};

const asArray = (value) => (Array.isArray(value) ? value : []);
const looksLikeEdge = (row) =>
  !!row && ("from_node_type" in row || "to_node_type" in row);

// Every field below is looked for across ALL THREE payloads rather than in the
// one whose name matches it. The endpoints are undocumented and the sample we
// have carries `items` and `connectors` in the SAME body, so binding a field to
// one response would break the moment the split differs from the guess.
const collectItems = (payloads) => {
  const out = [];
  const seen = new Set();
  payloads.forEach((payload) => {
    const candidates = [
      asArray(payload?.items),
      asArray(payload?.shortlist),
      Array.isArray(payload) && !looksLikeEdge(payload[0]) ? payload : [],
      asArray(payload?.sections).flatMap((section) => asArray(section?.items)),
    ];
    candidates.flat().forEach((item) => {
      const id = item?.execution_blueprint_item_id;
      if (id == null) return;
      const existing = seen.has(id)
        ? out.find((r) => r.execution_blueprint_item_id === id)
        : null;
      if (existing) {
        // Later payloads fill gaps but never overwrite: the sections response is
        // the authority on an item's text, the shortlist on its Year 1 fields,
        // and neither should clobber the other's.
        Object.keys(item).forEach((k) => {
          if (existing[k] == null && item[k] != null) existing[k] = item[k];
        });
        return;
      }
      seen.add(id);
      out.push({ ...item });
    });
  });
  return out;
};

const collectEdges = (payloads) => {
  for (const payload of payloads) {
    const candidate =
      (Array.isArray(payload?.connectors) && payload.connectors) ||
      (Array.isArray(payload?.edges) && payload.edges) ||
      (Array.isArray(payload?.connectors?.edges) && payload.connectors.edges) ||
      (Array.isArray(payload) && looksLikeEdge(payload[0]) && payload) ||
      null;
    if (candidate) return candidate;
  }
  return [];
};

const firstDefined = (payloads, key) => {
  for (const payload of payloads) {
    if (payload && payload[key] != null) return payload[key];
  }
  return null;
};

export const normalizeLockedBlueprint = (
  sectionsRes,
  connectorsRes,
  shortlistRes,
) => {
  const payloads = [sectionsRes, connectorsRes, shortlistRes].filter(Boolean);

  // ── sections ──────────────────────────────────────────────────────────────
  const grouped = payloads.find(
    (p) => Array.isArray(p?.sections) && p.sections.length,
  )?.sections;

  const items = collectItems(payloads);
  const sections = grouped
    ? grouped
    : (() => {
        const bySection = new Map();
        items.forEach((item) => {
          const key = item.section_key || "unknown";
          if (!bySection.has(key)) bySection.set(key, []);
          bySection.get(key).push(item);
        });
        return [...bySection.entries()]
          .map(([section_key, sectionItems]) => ({
            section_key,
            label: SECTION_LABEL[section_key] || section_key,
            sequence: SECTION_SEQUENCE.indexOf(section_key) + 1 || 99,
            items: sectionItems,
          }))
          .sort((a, b) => a.sequence - b.sequence);
      })();

  // ── connectors ────────────────────────────────────────────────────────────
  const edges = collectEdges(payloads);
  const graphNodes = firstDefined(payloads, "nodes");
  const bhagText = firstDefined(payloads, "bhag");
  const referencesBhag = edges.some(
    (e) => e?.from_node_type === "bhag" || e?.to_node_type === "bhag",
  );

  const connectors =
    edges.length || graphNodes
      ? {
          nodes: {
            bhag:
              graphNodes?.bhag ||
              (bhagText
                ? {
                    label:
                      typeof bhagText === "string" ? bhagText : bhagText.label,
                  }
                : referencesBhag
                  ? { label: "" }
                  : null),
            items:
              graphNodes?.items ||
              items.map((item) => ({
                execution_blueprint_item_id: item.execution_blueprint_item_id,
                section_key: item.section_key,
                label: item.label,
              })),
          },
          edges,
          overview: firstDefined(payloads, "overview") || {
            total_wigs: items.length,
            ai_suggested_count: edges.filter(
              (e) => e?.source && e.source !== "manual",
            ).length,
            total_connections: edges.length,
          },
        }
      : null;

  // ── Year 1 overlay ────────────────────────────────────────────────────────
  // The gap-fill above never overwrites, which is right for an item's text but
  // WRONG for the Year 1 fields: `is_year1_shortlisted: false` is a real value,
  // not a gap, so a `true` arriving later from /locked/shortlist would be
  // ignored. The shortlist response is the authority on these four fields, so
  // it is applied last and explicitly.
  const shortlistRows = [
    ...asArray(shortlistRes?.shortlist),
    ...asArray(shortlistRes?.items),
    ...(Array.isArray(shortlistRes) && !looksLikeEdge(shortlistRes[0])
      ? shortlistRes
      : []),
  ];
  if (shortlistRows.length) {
    const byItemId = new Map(
      shortlistRows
        .filter((row) => row?.execution_blueprint_item_id != null)
        .map((row) => [row.execution_blueprint_item_id, row]),
    );
    sections.forEach((section) => {
      (section.items || []).forEach((item) => {
        const row = byItemId.get(item.execution_blueprint_item_id);
        if (!row) return;
        if ("is_year1_shortlisted" in row)
          item.is_year1_shortlisted = row.is_year1_shortlisted;
        // ── Owners (handoff #25 §4, 2026-09-14) ──
        // The three locked endpoints now carry `owner_user_ids` / `owners`
        // alongside the singular pair, which they keep as "the first owner".
        // All four are copied: the plural fields so a screen can show several
        // owners, the singular ones because that is what the locked view and
        // the PDF read today — #25 explicitly keeps the PDF single-owner for
        // now, so dropping them would silently blank the owner column there.
        if ("owner_user_ids" in row) item.owner_user_ids = row.owner_user_ids;
        if ("owners" in row) item.owners = row.owners;
        if ("owner_user_id" in row) item.owner_user_id = row.owner_user_id;
        if ("owner_name" in row) item.owner_name = row.owner_name;
        if ("owner_suggested_by_agent" in row)
          item.owner_suggested_by_agent = row.owner_suggested_by_agent;
      });
    });
  }

  return {
    sections,
    connectors,
    roster: asArray(firstDefined(payloads, "roster")),
    bhag: typeof bhagText === "string" ? bhagText : (bhagText?.label ?? null),
    locked_at: firstDefined(payloads, "locked_at"),
    blueprint_state: firstDefined(payloads, "blueprint_state"),
  };
};
export const useExecutionBlueprintStore = createStore((set, get) => ({
  dashboardLoading: false,
  clients: null,

  // Post-lock reads, cached per blueprint. A locked blueprint is immutable by
  // definition, so unlike the live screens this cache never needs invalidating
  // — `{ force }` exists only for a manual retry after a failed fetch.
  lockedByUniqueId: {},
  lockedLoading: false,
  lockedError: null,
  // The PDF download. Separate from `lockedLoading`, which is the READ that
  // populates the screen — a download in flight must not make the screen look
  // like it is still loading its data.
  lockedDownloading: false,
  lockedDownloadError: null,

  _resolveAuthHeader: () => {
    const accessToken = getCookie(ACCESS_TOKEN_COOKIE_KEY);
    const adminAccessToken = getCookie(ADMIN_ACCESS_TOKEN_COOKIE_KEY);
    const adminsCoachId = getCookie(ADMINS_COACH_ID_COOKIE_KEY);
    if (accessToken) return `Bearer ${accessToken}`;
    if (adminAccessToken && adminsCoachId) return `Bearer ${adminAccessToken}`;
    return null;
  },

  _get: async (url) => {
    const authHeader = get()._resolveAuthHeader();
    return nodeInstance.get(url, {
      headers: authHeader ? { Authorization: authHeader } : {},
    });
  },

  // Same as `_get` but for a file response. Split out rather than adding a
  // flag: `responseType: "blob"` changes how axios reports ERRORS too — a
  // JSON 409 comes back as a Blob — so the two paths have different failure
  // handling and are better kept apart. See `parseBlobErrorMessage`.
  _getBlob: async (url) => {
    const authHeader = get()._resolveAuthHeader();
    return nodeInstance.get(url, {
      headers: authHeader ? { Authorization: authHeader } : {},
      responseType: "blob",
    });
  },

  _post: async (url, body = {}) => {
    const authHeader = get()._resolveAuthHeader();
    return nodeInstance.post(url, body, {
      headers: authHeader ? { Authorization: authHeader } : {},
    });
  },

  // The handoff doc (08-fe-handoff.md §4) shows the per-row shape but not the
  // list envelope. Assumed to match Strategy Canvas's own coach dashboard
  // (`{ data: { clients: [...] } }`) since both are CoachAuthGuard routes on
  // the same backend; falls back to a bare array under `data` if that
  // assumption is wrong, rather than throwing. Verify against the live
  // endpoint and update this comment (and PROGRESS.md) once confirmed.
  fetchDashboard: async () => {
    set({ dashboardLoading: true });
    try {
      const response = await get()._get(executionBlueprintUrls.GET_DASHBOARD);
      const rawClients = Array.isArray(response.data?.data?.clients)
        ? response.data.data.clients
        : Array.isArray(response.data?.data)
          ? response.data.data
          : [];
      const clients = rawClients.map(mapDashboardClient);
      set({ clients, dashboardLoading: false });
    } catch (error) {
      errorNoti(
        error.response?.data?.message ||
          "Couldn't load the dashboard",
      );
      set({ clients: [], dashboardLoading: false });
    }
  },

  // Called by the dashboard's "Start Review" CTA before navigating into
  // Review Mode - idempotent on the backend (COACH-API.md §1), so safe to
  // call every time regardless of whether the coach already entered review.
  // Failure here shouldn't block navigation - mirrors Strategy Canvas's own
  // enterReview, whose comment explains the same reasoning: the coach still
  // needs to reach the review screen, which is the actual destination.
  enterReview: async (uniqueId) => {
    if (!uniqueId) return;
    try {
      await get()._post(
        executionBlueprintUrls.POST_REVIEW_ENTER.replace(
          "{uniqueId}",
          uniqueId,
        ),
      );
    } catch (error) {
      errorNoti(error.response?.data?.message || "Couldn't open review mode");
    }
  },

  // ── The Final Locked Blueprint screens' only read ─────────────────────────
  // Three GETs in parallel, one normalised object out. Parallel rather than
  // sequential because they are independent reads of an immutable record;
  // `allSettled`, not `all`, so one missing endpoint degrades that tab instead
  // of blanking all three.
  //
  // ⚠️ Does NOT call `errorNoti`. Every other read here does, but this one's
  // failure is the whole screen rather than a corner of it, so it is rendered
  // in place — a toast over an empty page tells someone what happened and then
  // takes the explanation away again.
  // ── The locked blueprint's PDF ──────────────────────────────────────────
  // ⚠️ SERVER-GENERATED (2026-09-03). This replaces `lockedBlueprintPdf.js`,
  // which built the PDF in the browser with html2canvas + jsPDF — ~500 lines
  // that took three rounds of fixes (a cropped wall chart, unreadable rotated
  // band rails, a double-scaled connector layer) because it was photographing
  // a live DOM and inheriting every one of that DOM's quirks. The endpoint
  // renders all three tabs in one document, so the screen has one button.
  //
  // Reports through `errorNoti` like every other write here — the locked view's
  // own inline error is kept as well, since the button sits far from wherever
  // the toast appears.
  downloadLockedBlueprint: async (uniqueId) => {
    if (!uniqueId || get().lockedDownloading) return false;
    set({ lockedDownloading: true });
    try {
      const response = await get()._getBlob(
        executionBlueprintUrls.LOCKED_DOWNLOAD.replace("{uniqueId}", uniqueId),
      );
      triggerFileDownload(
        new Blob([response.data], { type: "application/pdf" }),
        "execution-blueprint.pdf",
      );
      return true;
    } catch (error) {
      const message = await parseBlobErrorMessage(
        error,
        "Couldn't download the Execution Blueprint PDF",
      );
      errorNoti(message);
      set({ lockedDownloadError: message });
      return false;
    } finally {
      set({ lockedDownloading: false });
    }
  },

  fetchLockedBlueprint: async (uniqueId, { force = false } = {}) => {
    if (!uniqueId) return null;
    if (!force && get().lockedByUniqueId[uniqueId]) {
      return get().lockedByUniqueId[uniqueId];
    }

    if (!LOCKED_ENDPOINTS_READY) {
      set({
        lockedLoading: false,
        lockedError: { code: "endpoints_not_wired" },
      });
      return null;
    }

    set({ lockedLoading: true, lockedError: null });
    const url = (template) => template.replace("{uniqueId}", uniqueId);
    try {
      const [sections, connectors, shortlist] = await Promise.allSettled([
        get()._get(url(executionBlueprintUrls.LOCKED_SECTIONS)),
        get()._get(url(executionBlueprintUrls.LOCKED_CONNECTORS)),
        get()._get(url(executionBlueprintUrls.LOCKED_SHORTLIST)),
      ]);

      // All three failing means the blueprint (or the whole surface) is
      // unreachable — that is an error. A partial failure is not: it means one
      // tab has nothing to show, which the tab itself already renders honestly.
      if (
        sections.status === "rejected" &&
        connectors.status === "rejected" &&
        shortlist.status === "rejected"
      ) {
        throw sections.reason;
      }

      const blueprint = normalizeLockedBlueprint(
        sections.status === "fulfilled" ? unwrap(sections.value) : null,
        connectors.status === "fulfilled" ? unwrap(connectors.value) : null,
        shortlist.status === "fulfilled" ? unwrap(shortlist.value) : null,
      );
      set((state) => ({
        lockedByUniqueId: { ...state.lockedByUniqueId, [uniqueId]: blueprint },
        lockedLoading: false,
      }));
      return blueprint;
    } catch (error) {
      set({
        lockedLoading: false,
        lockedError: {
          code: error.response?.status === 404 ? "not_found" : "failed",
          message:
            error.response?.data?.message ||
            "We couldn't load this locked blueprint.",
        },
      });
      return null;
    }
  },
}));

export default useExecutionBlueprintStore;

// React hook wrapper to subscribe to the vanilla store and trigger re-renders
// - same pattern as Strategy Canvas's own store (see CLAUDE.md).
export const useExecutionBlueprintStoreSelector = (selector) => {
  const [selected, setSelected] = useState(() =>
    selector(useExecutionBlueprintStore.getState()),
  );
  useEffect(() => {
    const unsub = useExecutionBlueprintStore.subscribe((state) => {
      setSelected(() => selector(state));
    });
    return () => unsub();
  }, [selector]);
  return selected;
};
